import BigNumber from 'bignumber.js'
import { match } from 'ts-pattern'

import { BigNumber as BigNumber_, Order } from '@blade/std-lib'
import { UserMinimal } from '@root/entities/user-short/model'

import { MakerAddress } from './maker-address'

import type { ShortTx } from '@blade/blockchain-types'
import type { TradeApi } from '@root/shared/api'
import type { AvatarsHost } from '@root/shared/models'

export type MakerSize = 'shrimp' | 'dolphin' | 'whale' | 'fish'
export type MakerTag = NonNullable<TradeApi.TxMakerDto['tags']>[number]

export interface MakerShort {
  address: MakerAddress
  /**
   * Transaction count within pool
   */
  txCount: number
  buyVolumeInUsd: BigNumber
  buyVolumeCurrency: BigNumber
  sellVolumeInUsd: BigNumber
  sellVolumeCurrency: BigNumber
  volumeInUsd: BigNumber
  balanceCurrency: BigNumber
  firstTradeAt: Date
  pnl: BigNumber
  realizedPnlUsd: BigNumber
  unrealizedPnlUsd: BigNumber
  avgBuyMarketCap: BigNumber | undefined
  avgSellMarketCap: BigNumber | undefined
  avgBuyPrice: BigNumber | undefined
  avgSellPrice: BigNumber | undefined
  buys: number
  sells: number
  tags: Array<MakerTag>
  owner: UserMinimal.UserMinimal | undefined
}

export interface Maker extends MakerShort {
  balanceInNative: BigNumber
  balanceInUsd: BigNumber
  balanceInUsdPercentage: BigNumber

  firstTradeAt: Date
  lastTradeAt: Date
  winRate30D: BigNumber | undefined
  pnl30D: BigNumber | undefined
}

export interface RankedMaker extends Maker {
  rank: number
}

export interface TradeMaker extends MakerShort {
  label?: string
  balanceCurrencyBefore: BigNumber
}

export const makerSize = (volume: BigNumber) => {
  return match(volume)
    .returnType<MakerSize>()
    .when(
      (vol) => vol.gte(10_000),
      () => 'whale',
    )
    .when(
      (vol) => vol.gte(1000),
      () => 'dolphin',
    )
    .when(
      (vol) => vol.gte(250),
      () => 'shrimp',
    )
    .otherwise(() => 'fish')
}

export const fromShortTxDto = (
  dto: ShortTx['maker'],
  avatarsHost: AvatarsHost,
): MakerShort => {
  return {
    address: MakerAddress(dto.address),
    balanceCurrency: BigNumber(dto.remainingInToken),
    buys: Number(dto.buys),
    sells: Number(dto.sells),
    txCount: Number(dto.txCount),
    buyVolumeCurrency: BigNumber(dto.buyVolumeInToken),
    sellVolumeCurrency: BigNumber(dto.sellVolumeInToken),
    buyVolumeInUsd: BigNumber(dto.buyVolumeInUsd),
    sellVolumeInUsd: BigNumber(dto.sellVolumeInUsd),
    volumeInUsd: BigNumber(dto.volumeInUsd),
    pnl: BigNumber(dto.pnl),
    realizedPnlUsd: BigNumber(dto.rPnl),
    unrealizedPnlUsd: BigNumber(dto.uPnl),
    avgBuyMarketCap: dto.avgBuyMarketCap
      ? BigNumber(dto.avgBuyMarketCap)
      : undefined,
    avgSellMarketCap: dto.avgSellMarketCap
      ? BigNumber(dto.avgSellMarketCap)
      : undefined,
    avgBuyPrice: dto.avgBuyPrice ? BigNumber(dto.avgBuyPrice) : undefined,
    avgSellPrice: dto.avgSellPrice ? BigNumber(dto.avgSellPrice) : undefined,
    firstTradeAt: new Date(dto.firstTxAt),
    tags: dto.tags ?? [],
    owner: dto.owner ? UserMinimal.fromDto(dto.owner, avatarsHost) : undefined,
  }
}
export const shortFromDto = (
  dto: TradeApi.TxMakerDto,
  avatarsHost: AvatarsHost,
): MakerShort => {
  return {
    address: MakerAddress(dto.address),
    balanceCurrency: BigNumber(dto.balanceInToken),
    buys: Number(dto.buys),
    sells: Number(dto.sells),
    txCount: Number(dto.txes),
    buyVolumeCurrency: BigNumber(dto.buyVolumeInToken),
    sellVolumeCurrency: BigNumber(dto.sellVolumeInToken),
    buyVolumeInUsd: BigNumber(dto.buyVolumeInUsd),
    sellVolumeInUsd: BigNumber(dto.sellVolumeInUsd),
    volumeInUsd: BigNumber(dto.volumeInUsd),
    pnl: BigNumber(dto.pnl),
    realizedPnlUsd: BigNumber(dto.rPnl),
    unrealizedPnlUsd: BigNumber(dto.uPnl),
    avgBuyMarketCap: undefined,
    avgSellMarketCap: undefined,
    avgBuyPrice: undefined,
    avgSellPrice: undefined,
    firstTradeAt: new Date(dto.firstTxAt),
    tags: dto.tags ?? [],
    owner: dto.owner ? UserMinimal.fromDto(dto.owner, avatarsHost) : undefined,
  }
}

export const fromDto = (
  dto: TradeApi.TxMakerDto,
  avatarsHost: AvatarsHost,
): Maker => {
  return {
    ...shortFromDto(dto, avatarsHost),
    balanceInNative: BigNumber(dto.balanceInNative),
    balanceInUsd: BigNumber(dto.balanceInUsd),
    balanceInUsdPercentage: BigNumber(dto.balanceInUsdPercentage),
    lastTradeAt: new Date(dto.lastTxAt),
    winRate30D: dto.winRate30D ? BigNumber(dto.winRate30D) : undefined,
    pnl30D: dto.pnl30D ? BigNumber(dto.pnl30D) : undefined,
  }
}

export const fromRankedDto = (
  dto: TradeApi.ServiceMakerHolderDto,
  avatarsHost: AvatarsHost,
): RankedMaker => {
  return {
    ...fromDto(dto, avatarsHost),
    rank: dto.rank,
  }
}

export const cmpByBalanceInToken = Order.mapInput(
  BigNumber_.order,
  (maker: Pick<Maker, 'balanceCurrency'>) => maker.balanceCurrency,
)

export const cmpByBalanceInTokenDesc = Order.flip(cmpByBalanceInToken)
